import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  Icon,
  Icons,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader
} from '@clientbase/clientbase-library'
import {
  Box,
  Grid,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material'
import { DataService } from 'api/DataService'
import copy from 'copy-to-clipboard'
import CopyLinkPopover from 'features/coupons/components/CopyLinkPopover/CopyLinkPopover'
import CouponsAccordion from 'features/coupons/components/CouponsAccordion'
import { useCanUseCoupons } from 'features/coupons/hooks/useCanUseCoupons'
import { Coupon } from 'features/coupons/types/coupon'
import historicChangesMapper, {
  Historic as HistoricType
} from 'models/Historic/HistoricChanges'
import {
  amountTypeEnum,
  issueWhenEnum,
  natureOperationEnum
} from 'models/Invoice'
import { ProductLink } from 'models/ProductLink'
import moment from 'moment'
import { useRouter } from 'next/router'

import Can from 'components/Can'
import ChargeFineAndInterest from 'components/ChargeFineAndInterest'
import IconContainer from 'components/IconContainer/IconContainer'
import InstallmentDetails from 'components/InstallmentDetails'
import ModalNewPaymentLink from 'components/Modal/ModalNewPaymentLink/components'
import ModalPaymentReport from 'components/Modal/ModalPaymentLinkReport'
import { getAdjustmentTypeLabel } from 'components/Modal/ModalRecurrenceDetail/ModalRecurrenceDetail.mappers'
import { Table } from 'components/Table'
import ChipTag from 'components/Tag'
import { Tag } from 'components/TagsAddInput/TagsAddInput'
import TagsPopover from 'components/TagsPopover'

import { formatCpfCnpj, formatMoney, formatPhone } from 'utils'
import { convertToCustomPortalUrl } from 'utils/domain-urls'
import { describeFrequency } from 'utils/frequency'
import { convertTagsStringToArray } from 'utils/tags'

import { theme } from 'styles/theme'

import {
  renderItemBonus,
  renderItemTotal
} from '../ModalChargeDetail/components/Composition/Composition'
import * as S from './ModalPaymentLinkDetail.styles'

type ModalPaymentDetailProps = {
  productLink: ProductLink
  reloadTable?: React.Dispatch<React.SetStateAction<boolean>>
  handleCloseModalDetailPayment: (value: boolean) => void
  isOpenModalPaymentDetail: boolean
  updateTable?: () => void
}

interface TagForm {
  tags: Tag[]
}

// Função auxiliar para criar títulos dos cards (baseada no modal de recorrência)
const cardTittle = (label: string, icon: Icons, iconColor?: string) => (
  <Box display="flex" alignItems="center">
    <Box display="flex" alignItems="center" gap="8px">
      <S.ContainerIcon>
        <Icon icon={icon} colorSX={iconColor} />
      </S.ContainerIcon>
      <Typography variant="smLight">{label}</Typography>
    </Box>
  </Box>
)

// Função para mapear composição (baseada no modal de cobrança/recorrência)
const compositionDetailMapper = (items: any[]) => {
  if (!items || !Array.isArray(items)) return []

  return items.map((item) => ({
    item: item.product ? (
      <a
        target="_blank"
        href={`/planos/${item.product.uuid}`}
        style={{ all: 'unset' }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography>{item.product.name}</Typography>
          <Typography color={theme.palette.neutral[400]} fontWeight={600}>
            {(item.product as any).total_cycles == 0 ? (
              <Icon icon="cached" colorSX={theme.palette.neutral[400]} />
            ) : (
              `(${(item.product as any).total_cycles}x)`
            )}
          </Typography>
          <Icon
            icon="open"
            colorSX={theme.palette.neutral[400]}
            sx={{ cursor: 'pointer' }}
          />
        </Stack>
      </a>
    ) : typeof item.description === 'string' ? (
      item.description
    ) : (
      item.description?.title ||
      item.description?.description ||
      item.title ||
      'Item sem nome'
    ),
    amountUnit: formatMoney(Number(item.amount_unit || item.price || 0)),
    bonus: renderItemBonus(item),
    quantity: parseInt(item.quantity ?? 0, 10),
    modifyQuantity: item.enable_customer_select_quantity
      ? 'Permitido'
      : 'Não permitido',
    minimumQuantity: item.enable_customer_select_quantity
      ? item.minimum_quantity ?? 1
      : '-',
    amountBilled: renderItemTotal({
      ...item,
      amount_billed: Number(item.amount_billed || item.total || 0)
    })
  }))
}

// Função para mapear formas de pagamento
const getChargeEnum = (status: string) => {
  switch (status) {
    case 'boleto':
      return 'Boleto'
    case 'cash':
      return 'Dinheiro'
    case 'credit_card':
      return 'Cartão de crédito'
    case 'other':
      return 'Outros'
    case 'pix':
      return 'Pix'
    case 'transfer':
      return 'Transferência (TED/DOC)'
    case 'credit_card_recurring':
      return 'Cartão recorrente'
    case 'credit_card_installments':
      return 'Cartão parcelado'
    case 'boleto_pix':
      return 'Pix'
    default:
      return 'Status desconhecido'
  }
}

// Função para mapear frequência
const getFrequency = (
  status: string,
  frequencyType?: string,
  frequencyNumber?: number
) => {
  switch (status) {
    case 'custom':
      // Personalizada: descreve o par canônico ("A cada 10 dias")
      return (
        describeFrequency(status, frequencyType, frequencyNumber) ||
        'Personalizada'
      )
    case 'weekly':
      return 'Semanal'
    case 'biweekly':
      return 'Quinzenal'
    case 'monthly':
      return 'Mensal'
    case 'bimonthly':
      return 'Bimestral'
    case 'quarterly':
      return 'Trimestral'
    case 'semester':
      return 'Semestral'
    case 'annual':
      return 'Anual'
    default:
      return 'Status desconhecido'
  }
}

// Frequência do REAJUSTE — lista diferente da frequência de cobrança (getFrequency acima).
const getAdjustmentFrequencyLabel = (frequency: string) => {
  switch (frequency) {
    case 'quarterly':
      return 'Trimestral'
    case 'semester':
      return 'Semestral'
    case 'annual':
      return 'Anual'
    default:
      return 'Desconhecida'
  }
}

// No link não há data: cada cliente contrata num dia. Mostra a distância até o reajuste.
const getAdjustmentRelativeLabel = (frequency: string) => {
  switch (frequency) {
    case 'quarterly':
      return '3 meses após a contratação'
    case 'semester':
      return '6 meses após a contratação'
    case 'annual':
      return '1 ano após a contratação'
    default:
      return 'Após a contratação'
  }
}

const ModalPaymentLinkDetail = ({
  productLink,
  handleCloseModalDetailPayment,
  isOpenModalPaymentDetail,
  updateTable
}: ModalPaymentDetailProps) => {
  const canUseCoupons = useCanUseCoupons()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
  const [isOpenModalPaymentReport, setIsOpenModalPaymentReport] =
    useState(false)
  const [paymentLinkV3Data, setPaymentLinkV3Data] = useState<any>(null)
  const [loadingModal, setLoadingModal] = useState<boolean>(true)
  const [detailCoupons, setDetailCoupons] = useState<Coupon[]>([])
  const { query } = useRouter()

  // Form for TagsPopover
  const { control, setValue } = useForm<TagForm>({
    defaultValues: {
      tags: []
    }
  })

  // Função para recarregar os dados do payment link
  const fetchPaymentLinkDetails = async () => {
    try {
      setLoadingModal(true)

      const response = await DataService({
        url: `/v3/product_links/${
          productLink.uuid || query?.payment_link_uuid
        }`,
        type: 'GET'
      })

      // Armazenar os dados da API v3 para usar na edição
      if (response?.data?.record) {
        setPaymentLinkV3Data(response.data.record)

        // Convert tags to the proper format and set them in the form for TagsPopover
        const tagsData = response.data.record?.tags || productLink?.tags
        if (tagsData) {
          const convertedTags = convertTagsStringToArray(tagsData, [])
          setValue('tags', convertedTags)
        }

        // Populate coupons
        setDetailCoupons(response.data.record?.coupons || [])
      }
      setLoadingModal(false)
    } catch (error) {
      console.error('❌ Erro ao buscar detalhes do payment link:', error)
      setLoadingModal(false)
    }
  }

  const isMobile = useMediaQuery('(max-width: 600px)')

  // useEffect para fazer GET na API v3 quando o modal abrir
  useEffect(() => {
    if (isOpenModalPaymentDetail && productLink?.uuid) {
      fetchPaymentLinkDetails()
    } else if (!isOpenModalPaymentDetail) {
      // Limpar dados quando o modal fechar para evitar problemas de memória
      setPaymentLinkV3Data(null)
      setLoadingModal(true)
    }
  }, [isOpenModalPaymentDetail, productLink?.uuid])

  const copyUuid = () => {
    copy(productLink?.uuid || '')
    toast.success('Identificador copiado para a área de transferência')
  }

  // Função para formatar o tipo de vencimento (igual à da tela de produtos)
  const verifyTypeChargeType = (dueType?: string, dueDay?: number): string => {
    switch (dueType) {
      case 'immediate_charge':
        return `No dia da contratação`
      case 'delay_charge':
        return `Após ${dueDay} ${(dueDay || 1) > 1 ? 'dias' : 'dia'}`
      case 'specified_charge':
        return `Todo dia ${dueDay}`
      case 'immediate_charge_and_specified_charge':
        return `No dia da contratação + Todo dia ${dueDay} (a partir do próximo mês)`
      case 'immediate_charge_and_same_month_specified_charge':
        return `No dia da contratação + Todo dia ${dueDay} (já neste mês)`
      case 'delay_charge_and_specified_charge':
        return `No dia da contratação + Após ${dueDay} ${
          (dueDay || 1) > 1 ? 'dias' : 'dia'
        }`
      default:
        return ''
    }
  }

  const statusEnum = {
    active: {
      fontColor: theme.palette.info[200],
      color: theme.palette.secondary[600],
      label: 'Ativo'
    },
    finished: {
      fontColor: theme.palette.neutral[400],
      color: theme.palette.neutral[800],
      label: 'Finalizado'
    },
    paused: {
      color: theme.palette.alert[400],
      fontColor: theme.palette.alert[200],
      label: 'Pausado'
    }
  }

  const handleOpen = async () => {
    const link = productLink?.link || paymentLinkV3Data?.link
    if (!link) return
    window.open(convertToCustomPortalUrl(link), '_blank')
  }

  const handleDuplicate = () => {
    setIsDuplicateModalOpen(true)
  }

  // Não renderizar se não há productLink
  if (!productLink?.uuid) {
    return null
  }

  return (
    <>
      <Modal
        handleClose={() => handleCloseModalDetailPayment(false)}
        open={isOpenModalPaymentDetail}
        width="100%"
        maxWidth="1150px"
        maxHeight="90vh"
        height="calc(100%-200px)"
        disablePortal={true}
        data-cy="payment-link-detail-modal"
      >
        <ModalBody>
          <ModalHeader onClose={() => handleCloseModalDetailPayment(false)}>
            <Stack
              width="100%"
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
            >
              <Tooltip
                title={`Identificador: ${
                  productLink?.uuid || paymentLinkV3Data?.uuid
                }`}
                placement="top"
                onClick={copyUuid}
                arrow
                PopperProps={{
                  onClick: copyUuid,
                  sx: { cursor: 'pointer' }
                }}
              >
                <div>
                  <Stack
                    sx={{ cursor: 'pointer' }}
                    direction="row"
                    alignItems="center"
                    gap={2}
                  >
                    <IconContainer icon="faCreditCard" />
                    <Typography variant="mdBold">
                      Detalhes do Link de Contratação
                    </Typography>
                  </Stack>
                </div>
              </Tooltip>

              {!loadingModal &&
              (productLink?.status || paymentLinkV3Data?.status) ? (
                <Chip
                  variant="success"
                  key={productLink?.uuid || paymentLinkV3Data?.uuid}
                  label={
                    statusEnum[
                      (productLink?.status ||
                        paymentLinkV3Data?.status) as keyof typeof statusEnum
                    ]?.label || 'Ativo'
                  }
                  sx={{
                    background:
                      statusEnum[
                        (productLink?.status ||
                          paymentLinkV3Data?.status) as keyof typeof statusEnum
                      ]?.color || theme.palette.secondary[600],
                    color:
                      statusEnum[
                        (productLink?.status ||
                          paymentLinkV3Data?.status) as keyof typeof statusEnum
                      ]?.fontColor || theme.palette.info[200],
                    height: '25px',
                    width: '160px',
                    marginRight: '10px',
                    [theme.breakpoints.down(508)]: {
                      width: '80px',
                      marginRight: '0px'
                    }
                  }}
                />
              ) : (
                <></>
              )}
            </Stack>
          </ModalHeader>

          {!loadingModal ? (
            <ModalContent>
              {/* Informações básicas do Payment Link */}
              <S.RecurrenceContent>
                <Grid
                  display="flex"
                  flexWrap="wrap"
                  gap={1.25}
                  alignItems="end"
                >
                  <Typography variant="smLight" color="#777777">
                    Título
                  </Typography>
                  <S.Separator sx={{ marginTop: '4px' }} />
                  <Typography
                    variant="smBold"
                    display="flex"
                    alignItems="center"
                  >
                    {typeof (paymentLinkV3Data?.title || productLink?.title) ===
                    'string'
                      ? paymentLinkV3Data?.title ||
                        productLink?.title ||
                        'Link de pagamento'
                      : paymentLinkV3Data?.title?.title ||
                        paymentLinkV3Data?.title?.description ||
                        productLink?.title ||
                        productLink?.description ||
                        'Link de pagamento'}
                  </Typography>
                </Grid>

                {/* Link direcionado a */}
                <Grid
                  display="flex"
                  flexWrap="wrap"
                  gap={1.25}
                  alignItems="end"
                >
                  <Typography variant="smLight" color="#777777">
                    Link direcionado a
                  </Typography>
                  <S.Separator sx={{ marginTop: '4px' }} />
                  <Typography
                    variant="smBold"
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                  >
                    {paymentLinkV3Data?.customer?.name ||
                    productLink?.customer?.name ? (
                      <>
                        <S.Link
                          onClick={() => {
                            // Navegar para o cliente
                            window.open(
                              `/clientes/${
                                paymentLinkV3Data?.customer?.uuid ||
                                productLink?.customer?.uuid
                              }`,
                              '_blank'
                            )
                          }}
                          style={{ textDecoration: 'underline' }}
                        >
                          {paymentLinkV3Data?.customer?.name ||
                            productLink?.customer?.name}
                        </S.Link>
                        <Icon
                          icon="open"
                          sx={{
                            cursor: 'pointer',
                            color: '#CBCBCB',
                            width: '18px',
                            height: '18px'
                          }}
                        />
                      </>
                    ) : (
                      'Novo cliente'
                    )}
                  </Typography>
                </Grid>

                {/* Person Type (Tipo de Cliente) - apenas para novos clientes */}
                {!paymentLinkV3Data?.customer?.name &&
                  !productLink?.customer?.name &&
                  paymentLinkV3Data?.document_type && (
                    <Grid
                      display="flex"
                      flexWrap="wrap"
                      gap={1.25}
                      alignItems="end"
                    >
                      <Typography variant="smLight" color="#777777">
                        Tipo de Cliente
                      </Typography>
                      <S.Separator sx={{ marginTop: '4px' }} />
                      <Typography variant="smBold">
                        {paymentLinkV3Data.document_type === 'cpf'
                          ? 'Pessoa Física'
                          : paymentLinkV3Data.document_type === 'cnpj'
                          ? 'Pessoa Jurídica'
                          : 'Ambos'}
                      </Typography>
                    </Grid>
                  )}

                <Grid
                  display="flex"
                  flexWrap="wrap"
                  gap={1.25}
                  alignItems="end"
                >
                  <Typography variant="smLight" color="#777777">
                    Valor total
                  </Typography>
                  <S.Separator sx={{ marginTop: '4px' }} />
                  <Typography
                    variant="smBold"
                    display="flex"
                    alignItems="center"
                  >
                    {formatMoney(
                      Number(
                        paymentLinkV3Data?.amount ||
                          productLink?.product?.amount ||
                          0
                      )
                    )}
                  </Typography>
                </Grid>

                {/* Periodicidade da cobrança */}
                <Grid
                  display="flex"
                  flexWrap="wrap"
                  gap={1.25}
                  alignItems="end"
                >
                  <Typography variant="smLight" color="#777777">
                    Cobrança
                  </Typography>
                  <S.Separator sx={{ marginTop: '4px' }} />
                  <Typography
                    variant="smBold"
                    display="flex"
                    alignItems="center"
                  >
                    {paymentLinkV3Data?.charge_type === 'single' ||
                    productLink?.chargeType === 0
                      ? 'Única'
                      : 'Recorrente'}
                  </Typography>
                </Grid>

                {/* Vencimento - só mostra se tiver dueType */}
                {(paymentLinkV3Data?.due_type || productLink?.dueType) && (
                  <Grid
                    display="flex"
                    flexWrap="wrap"
                    gap={1.25}
                    alignItems="end"
                  >
                    <Typography variant="smLight" color="#777777">
                      Vencimento
                    </Typography>
                    <S.Separator sx={{ marginTop: '4px' }} />
                    <Typography
                      variant="smBold"
                      display="flex"
                      alignItems="center"
                    >
                      {verifyTypeChargeType(
                        paymentLinkV3Data?.due_type || productLink?.dueType,
                        paymentLinkV3Data?.due_day || productLink?.dueDay
                      )}
                    </Typography>
                  </Grid>
                )}

                {/* Cobrança proporcional - só mostra se for true */}
                {(paymentLinkV3Data?.prorated_charge ||
                  productLink?.proratedCharge) && (
                  <Grid
                    display="flex"
                    flexWrap="wrap"
                    gap={1.25}
                    alignItems="end"
                  >
                    <Typography variant="smLight" color="#777777">
                      Cobrança proporcional
                    </Typography>
                    <S.Separator sx={{ marginTop: '4px' }} />
                    <Typography
                      variant="smBold"
                      display="flex"
                      alignItems="center"
                    >
                      Sim
                    </Typography>
                  </Grid>
                )}

                {/* Seção de Descontos - Inline como em cobrança/recorrência */}
                {paymentLinkV3Data?.discounts?.length > 0 ? (
                  paymentLinkV3Data.discounts.map(
                    (discount: any, index: number) => (
                      <Box
                        key={index}
                        display="flex"
                        gap={1.25}
                        alignItems="end"
                      >
                        <Typography variant="smLight" color="#777777">
                          {paymentLinkV3Data.discounts.length > 1
                            ? `Desconto ${index + 1}`
                            : 'Desconto'}
                        </Typography>
                        <S.Separator sx={{ marginTop: '4px' }} />
                        <Typography
                          variant="smBold"
                          display="flex"
                          alignItems="center"
                        >
                          <Box>
                            {discount.policy === 'percentage'
                              ? `${Number(discount.amount)
                                  .toFixed(2)
                                  .replace('.', ',')}%`
                              : formatMoney(Number(discount.amount))}{' '}
                            pagando até{' '}
                            {discount.days === 0
                              ? 'o dia do vencimento.'
                              : `${discount.days} dia(s) antes do vencimento.`}
                          </Box>
                        </Typography>
                      </Box>
                    )
                  )
                ) : (
                  <Box display="flex" gap={1.25} alignItems="end">
                    <Typography variant="smLight" color="#777777">
                      Desconto
                    </Typography>
                    <S.Separator sx={{ marginTop: '4px' }} />
                    <Typography variant="smBold">Não</Typography>
                  </Box>
                )}

                {/* Seção de Juros - Inline usando ChargeFineAndInterest */}
                <ChargeFineAndInterest
                  interestPolicy={
                    paymentLinkV3Data?.interest_policy ||
                    (productLink?.interestPolicy ? 'custom' : 'no_interest')
                  }
                  interestFine={
                    paymentLinkV3Data?.interest_fine ||
                    productLink?.interestFine ||
                    '0'
                  }
                  interestFee={
                    paymentLinkV3Data?.interest_fee ||
                    productLink?.interestFee ||
                    '0'
                  }
                  removeMargin
                />

                {/* Seção de Bonificação - Inline como em cobrança/recorrência */}
                {((paymentLinkV3Data?.bonus_policy !== 'no_bonus' &&
                  paymentLinkV3Data?.bonus) ||
                  productLink?.hasBonus) && (
                  <Box display="flex" gap={1.25} alignItems="end">
                    <Typography variant="smLight" color="#777777">
                      Bonificação
                    </Typography>
                    <S.Separator sx={{ marginTop: '4px' }} />
                    <Typography
                      variant="smBold"
                      display="flex"
                      alignItems="center"
                    >
                      {(() => {
                        // Dados da API v3 têm prioridade
                        if (
                          paymentLinkV3Data?.bonus_policy !== 'no_bonus' &&
                          paymentLinkV3Data?.bonus
                        ) {
                          const bonusType =
                            paymentLinkV3Data.bonus_policy === 'fixed'
                              ? 'value'
                              : 'percentage'
                          const bonusAmount = paymentLinkV3Data.bonus.amount
                          const bonusCycles =
                            paymentLinkV3Data.bonus.total_cycles

                          return `${
                            bonusType === 'value'
                              ? formatMoney(bonusAmount)
                              : Number(bonusAmount).toFixed(2).replace('.', ',')
                          }${
                            bonusType === 'percentage' ? '%' : ''
                          } por ${bonusCycles} ciclos`
                        }

                        // Fallback para dados do productLink
                        if (productLink?.bonusSettings) {
                          return `${
                            productLink.bonusSettings.type === 'value'
                              ? formatMoney(productLink.bonusSettings.amount)
                              : Number(productLink.bonusSettings.amount)
                                  .toFixed(2)
                                  .replace('.', ',')
                          }${
                            productLink.bonusSettings.type === 'percentage'
                              ? '%'
                              : ''
                          } por ${productLink.bonusSettings.cyclesCount} ciclos`
                        }

                        return 'Não configurado'
                      })()}
                    </Typography>
                  </Box>
                )}

                {(paymentLinkV3Data?.frequency || productLink?.frequency) && (
                  <Grid
                    display="flex"
                    flexWrap="wrap"
                    gap={1.25}
                    alignItems="end"
                  >
                    <Typography variant="smLight" color="#777777">
                      Frequência
                    </Typography>
                    <S.Separator sx={{ marginTop: '4px' }} />
                    <Typography
                      variant="smBold"
                      display="flex"
                      alignItems="center"
                    >
                      {getFrequency(
                        paymentLinkV3Data?.frequency ||
                          productLink?.frequency?.toString() ||
                          'monthly',
                        paymentLinkV3Data?.frequency_type,
                        paymentLinkV3Data?.frequency_number
                      )}
                    </Typography>
                  </Grid>
                )}

                {(paymentLinkV3Data?.total_cycles ||
                  productLink?.totalCycles) && (
                  <Box display="flex" gap={1.25} alignItems="end">
                    <Typography variant="smLight" color="#777777">
                      Ciclos
                    </Typography>
                    <S.Separator sx={{ marginTop: '4px' }} />
                    <Typography variant="smBold">
                      {paymentLinkV3Data?.total_cycles === 0 ||
                      productLink?.totalCycles === 0
                        ? 'Indeterminado'
                        : paymentLinkV3Data?.total_cycles ||
                          productLink?.totalCycles}
                    </Typography>
                  </Box>
                )}

                {(paymentLinkV3Data?.limit_days_for_payment ||
                  productLink?.limitDaysForPayment) && (
                  <Grid
                    display="flex"
                    flexWrap="wrap"
                    gap={1.25}
                    alignItems="end"
                  >
                    <Typography variant="smLight" color="#777777">
                      Dias para expirar
                    </Typography>
                    <S.Separator sx={{ marginTop: '4px' }} />
                    <Typography
                      variant="smBold"
                      display="flex"
                      alignItems="center"
                    >
                      {paymentLinkV3Data?.limit_days_for_payment ||
                        productLink?.limitDaysForPayment}
                    </Typography>
                  </Grid>
                )}

                {(paymentLinkV3Data?.issue_days ||
                  productLink?.limitDaysForPayment) && (
                  <Grid
                    display="flex"
                    flexWrap="wrap"
                    gap={1.25}
                    alignItems="end"
                  >
                    <Typography variant="smLight" color="#777777">
                      Dias para emitir a cobrança
                    </Typography>
                    <S.Separator sx={{ marginTop: '4px' }} />
                    <Typography
                      variant="smBold"
                      display="flex"
                      alignItems="center"
                    >
                      {paymentLinkV3Data?.issue_days}
                    </Typography>
                  </Grid>
                )}

                {/* Configurações específicas do Payment Link */}
                {paymentLinkV3Data?.enable_customer_select_quantity && (
                  <Grid
                    display="flex"
                    flexWrap="wrap"
                    gap={1.25}
                    alignItems="end"
                  >
                    <Typography variant="smLight" color="#777777">
                      Quantidade padrão
                    </Typography>
                    <S.Separator sx={{ marginTop: '4px' }} />
                    <Typography
                      variant="smBold"
                      display="flex"
                      alignItems="center"
                    >
                      {paymentLinkV3Data?.quantity_default || 1}
                    </Typography>
                  </Grid>
                )}

                <Grid
                  display="flex"
                  flexWrap="wrap"
                  gap={1.25}
                  alignItems="end"
                >
                  <Typography variant="smBold" color="#777777">
                    Meios de pagamento aceitos
                  </Typography>
                </Grid>

                <Box display="flex" mb="8px" flexWrap="wrap" gap={1}>
                  {(
                    paymentLinkV3Data?.payment_type?.split(';') ||
                    productLink?.payments ||
                    []
                  )
                    .filter(
                      (paymentMethod: string) => paymentMethod !== 'boleto_pix'
                    )
                    .map((paymentMethod: string, index: number) => {
                      if (!paymentMethod) return null
                      return (
                        <Box key={index}>
                          <Chip
                            variant="payment"
                            label={getChargeEnum(paymentMethod).toUpperCase()}
                          />
                        </Box>
                      )
                    })}
                </Box>

                {/* Detalhes de Parcelamento */}
                {(paymentLinkV3Data?.payment_type_details ||
                  paymentLinkV3Data?.product_link_installment ||
                  (productLink as any)?.payment_type_details) && (
                  <InstallmentDetails
                    paymentTypeDetails={
                      paymentLinkV3Data?.payment_type_details ||
                      (productLink as any)?.payment_type_details
                    }
                    productInstallment={
                      paymentLinkV3Data?.product_link_installment
                        ? {
                            uuid: paymentLinkV3Data.product_link_installment
                              .uuid,
                            maxInstallments:
                              paymentLinkV3Data.product_link_installment
                                .max_installments,
                            maxInstallmentsWithoutFee:
                              paymentLinkV3Data.product_link_installment
                                .max_installments_without_fee,
                            createdAt:
                              paymentLinkV3Data.product_link_installment
                                .created_at,
                            updatedAt:
                              paymentLinkV3Data.product_link_installment
                                .updated_at
                          }
                        : undefined
                    }
                    paymentTypes={
                      paymentLinkV3Data?.payment_type?.split(';') ||
                      productLink?.payments ||
                      []
                    }
                  />
                )}

                {(paymentLinkV3Data?.description ||
                  productLink?.description) && (
                  <Grid
                    display="flex"
                    direction="column"
                    flexWrap="wrap"
                    gap={1.25}
                  >
                    <Typography variant="smLight" color="#777777">
                      Descrição:
                    </Typography>

                    <Typography
                      display="flex"
                      alignItems="center"
                      sx={{ whiteSpace: 'pre-line' }}
                    >
                      {typeof (
                        paymentLinkV3Data?.description ||
                        productLink?.description
                      ) === 'string'
                        ? paymentLinkV3Data?.description ||
                          productLink?.description
                        : paymentLinkV3Data?.description?.title ||
                          paymentLinkV3Data?.description?.description ||
                          productLink?.description ||
                          'Sem descrição'}
                    </Typography>
                  </Grid>
                )}

                {(paymentLinkV3Data?.billing_description ||
                  productLink?.billing_description) && (
                  <Grid
                    display="flex"
                    direction="column"
                    flexWrap="wrap"
                    gap={1.25}
                  >
                    <Typography variant="smLight" color="#777777">
                      Descrição na cobrança:
                    </Typography>

                    <Typography
                      display="flex"
                      alignItems="center"
                      sx={{ whiteSpace: 'pre-line' }}
                    >
                      {typeof (
                        paymentLinkV3Data?.billing_description ||
                        productLink?.billing_description
                      ) === 'string'
                        ? paymentLinkV3Data?.billing_description ||
                          productLink?.billing_description
                        : paymentLinkV3Data?.billing_description?.title ||
                          paymentLinkV3Data?.billing_description?.description ||
                          productLink?.billing_description ||
                          'Sem descrição na cobrança'}
                    </Typography>
                  </Grid>
                )}
                {/* Tags Display - Only show when tags exist */}
                {(paymentLinkV3Data?.tags || productLink?.tags) && (
                  <Box>
                    <Typography variant="smLight" color="#777777">
                      Tags:
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                      {convertTagsStringToArray(
                        paymentLinkV3Data?.tags || productLink?.tags || '',
                        [] // We don't need available tags for display only
                      ).map((tag) => (
                        <ChipTag
                          key={tag.id}
                          tag={tag}
                          // No onDelete prop - this is display only
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </S.RecurrenceContent>

              {/* Seções em Accordions */}
              <S.AccordionWrapper>
                {/* Opções avançadas Link */}
                <div>
                  <Accordion
                    defaultExpanded
                    sx={{
                      border: '1px solid #E8EBE8',
                      borderLeft: '3px solid',
                      borderLeftColor: 'primary.main',
                      borderRadius: '8px !important',
                      backgroundColor: '#FAFBFA',
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        margin: 0,
                        backgroundColor: '#fff'
                      }
                    }}
                  >
                    <AccordionSummary
                      aria-controls="advancedOptions-content"
                      id="advancedOptions"
                      data-cy="advanced-options-accordion-summary"
                    >
                      {cardTittle('Opções avançadas Link', 'supervisorAccount')}
                    </AccordionSummary>
                    <AccordionDetails>
                      <div>
                        {/* Adicionar ao grupo */}
                        {((paymentLinkV3Data?.customer_groups?.length || 0) >
                          0 ||
                          (productLink?.customer_groups?.length || 0) > 0) && (
                          <Box
                            display="flex"
                            gap={1.25}
                            alignItems="end"
                            margin="10px 0px 10px 0px"
                          >
                            <Typography variant="smLight" color="#777777">
                              Adicionar ao grupo
                            </Typography>
                            <S.Separator sx={{ marginTop: '4px' }} />
                            <Typography
                              variant="smBold"
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                            >
                              {(
                                paymentLinkV3Data?.customer_groups ||
                                productLink?.customer_groups ||
                                []
                              )
                                .map((group: any) => (
                                  <span key={group.uuid}>
                                    <S.Link
                                      onClick={() => {
                                        // Navegar para o grupo
                                        window.open(
                                          `/grupos/${group.uuid}`,
                                          '_blank'
                                        )
                                      }}
                                      style={{ textDecoration: 'underline' }}
                                    >
                                      {group.name}
                                    </S.Link>
                                    <Icon
                                      icon="open"
                                      sx={{
                                        cursor: 'pointer',
                                        color: '#CBCBCB',
                                        width: '18px',
                                        height: '18px',
                                        ml: 0.5
                                      }}
                                    />
                                  </span>
                                ))
                                .reduce(
                                  (prev: any, curr: any, index: number) =>
                                    index === 0
                                      ? [curr]
                                      : [...prev, ', ', curr],
                                  []
                                )}
                            </Typography>
                          </Box>
                        )}

                        {/* URL de saída */}
                        {(paymentLinkV3Data?.custom_output_url ||
                          productLink?.custom_output_url) && (
                          <Box
                            display="flex"
                            gap={1.25}
                            alignItems="end"
                            margin="10px 0px 10px 0px"
                          >
                            <Typography variant="smLight" color="#777777">
                              URL de saída
                            </Typography>
                            <S.Separator sx={{ marginTop: '4px' }} />
                            <Typography variant="smBold">
                              {paymentLinkV3Data?.custom_output_url ||
                                productLink?.custom_output_url}
                            </Typography>
                          </Box>
                        )}

                        {/* URL personalizada */}
                        {(paymentLinkV3Data?.link || productLink?.link) && (
                          <Box
                            display="flex"
                            gap={1.25}
                            alignItems="end"
                            margin="10px 0px 10px 0px"
                          >
                            <Typography variant="smLight" color="#777777">
                              URL personalizada
                            </Typography>
                            <S.Separator sx={{ marginTop: '4px' }} />
                            <Typography variant="smBold">
                              {paymentLinkV3Data?.link
                                .replace(/^https?:\/\//, '')
                                .replace(/\/checkout\/[a-f0-9-]+$/i, '') ||
                                productLink?.link
                                  .replace(/^https?:\/\//, '')
                                  .replace(/\/checkout\/[a-f0-9-]+$/i, '')}
                            </Typography>
                          </Box>
                        )}

                        {/* Campos Extras */}
                        {(paymentLinkV3Data?.custom_field_configs?.length > 0 ||
                          paymentLinkV3Data?.extra_fields?.length > 0 ||
                          productLink?.extra_fields?.length > 0) && (
                          <>
                            {(
                              paymentLinkV3Data?.custom_field_configs ||
                              paymentLinkV3Data?.extra_fields ||
                              productLink?.extra_fields ||
                              []
                            ).map((field: any, index: number) => (
                              <Box
                                key={field.uuid || index}
                                display="flex"
                                gap={1.25}
                                alignItems="end"
                                margin="10px 0px 10px 0px"
                              >
                                <Typography variant="smLight" color="#777777">
                                  Campo Extra
                                </Typography>
                                <S.Separator sx={{ marginTop: '4px' }} />
                                <Typography variant="smBold">
                                  {typeof field.name === 'string'
                                    ? field.name
                                    : field.custom_field?.field_configuration
                                        ?.title ||
                                      field.field_configuration?.title ||
                                      field.title ||
                                      'Campo sem nome'}
                                </Typography>
                              </Box>
                            ))}
                          </>
                        )}

                        {/* Mensagem de confirmação */}
                        {(paymentLinkV3Data?.message ||
                          productLink?.message) && (
                          <>
                            <Box
                              display="flex"
                              gap={1.25}
                              alignItems="start"
                              margin="20px 0px 10px 0px"
                            >
                              <Typography variant="smBold" color="#777777">
                                Mensagem de confirmação
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                marginTop: '4px'
                              }}
                            >
                              <Typography
                                variant="smLight"
                                color={'#777777'}
                                sx={{ whiteSpace: 'pre-line' }}
                              >
                                {typeof (
                                  paymentLinkV3Data?.message ||
                                  productLink?.message
                                ) === 'string'
                                  ? paymentLinkV3Data?.message ||
                                    productLink?.message
                                  : paymentLinkV3Data?.message?.title ||
                                    paymentLinkV3Data?.message?.description ||
                                    productLink?.message ||
                                    'Sem mensagem'}
                              </Typography>
                            </Box>
                          </>
                        )}

                        {/* Atualização Cadastral */}
                        {!paymentLinkV3Data?.customer?.name &&
                          !productLink?.customer?.name &&
                          (paymentLinkV3Data as any)
                            ?.existing_document_behavior !== undefined && (
                            <Box
                              display="flex"
                              gap={1.25}
                              alignItems="end"
                              margin="10px 0px 10px 0px"
                            >
                              <Typography variant="smLight" color="#777777">
                                Atualização Cadastral
                              </Typography>
                              <S.Separator sx={{ marginTop: '4px' }} />
                              <Typography variant="smBold">
                                {(() => {
                                  const behavior = (paymentLinkV3Data as any)
                                    .existing_document_behavior
                                  if (
                                    behavior === 0 ||
                                    behavior === 'skip_customer'
                                  )
                                    return 'Não permitir'
                                  if (
                                    behavior === 1 ||
                                    behavior === 'create_customer'
                                  )
                                    return 'Criar novo cadastro'
                                  return 'Atualizar dados'
                                })()}
                              </Typography>
                            </Box>
                          )}

                        {/* Tornar ativo somente após o primeiro pagamento */}
                        <Box
                          display="flex"
                          gap={1.25}
                          alignItems="end"
                          margin="10px 0px 10px 0px"
                        >
                          <Typography variant="smLight" color="#777777">
                            Tornar ativo somente após o primeiro pagamento
                          </Typography>
                          <S.Separator sx={{ marginTop: '4px' }} />
                          <Typography variant="smBold">
                            {paymentLinkV3Data?.consider_active_on_registration ===
                            false
                              ? 'Sim'
                              : 'Não'}
                          </Typography>
                        </Box>

                        {/* Cancelar recorrência se a primeira fatura não for paga até a emissão da segunda */}
                        <Box
                          display="flex"
                          gap={1.25}
                          alignItems="end"
                          margin="10px 0px 10px 0px"
                        >
                          <Typography variant="smLight" color="#777777">
                            Cancelar recorrência se a primeira fatura não for
                            paga até a emissão da segunda
                          </Typography>
                          <S.Separator sx={{ marginTop: '4px' }} />
                          <Typography variant="smBold">
                            {paymentLinkV3Data?.cancel_if_first_billing_unpaid
                              ? 'Sim'
                              : 'Não'}
                          </Typography>
                        </Box>

                        {/* Enviar e-mail de boas-vindas para Portal do Cliente */}
                        <Box
                          display="flex"
                          gap={1.25}
                          alignItems="end"
                          margin="10px 0px 10px 0px"
                        >
                          <Typography variant="smLight" color="#777777">
                            Enviar e-mail de boas-vindas para Portal do Cliente
                          </Typography>
                          <S.Separator sx={{ marginTop: '4px' }} />
                          <Typography variant="smBold">
                            {paymentLinkV3Data?.send_customer_welcome ||
                            productLink?.send_customer_welcome
                              ? 'Sim'
                              : 'Não'}
                          </Typography>
                        </Box>

                        {/* Contratação simplificada (dispensar endereço) */}
                        <Box
                          display="flex"
                          gap={1.25}
                          alignItems="end"
                          margin="10px 0px 10px 0px"
                        >
                          <Typography variant="smLight" color="#777777">
                            Contratação simplificada (dispensar endereço)
                          </Typography>
                          <S.Separator sx={{ marginTop: '4px' }} />
                          <Typography variant="smBold">
                            {paymentLinkV3Data?.simplified_checkout ||
                            productLink?.simplified_checkout
                              ? 'Sim'
                              : 'Não'}
                          </Typography>
                        </Box>
                      </div>
                    </AccordionDetails>
                  </Accordion>
                </div>

                {/* Notificação */}
                {paymentLinkV3Data?.emails_to_notify && (
                  <div>
                    <Accordion
                      defaultExpanded
                      sx={{
                        border: '1px solid #E8EBE8',
                        borderLeft: '3px solid',
                        borderLeftColor: 'primary.main',
                        borderRadius: '8px !important',
                        backgroundColor: '#FAFBFA',
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': {
                          margin: 0,
                          backgroundColor: '#fff'
                        }
                      }}
                    >
                      <AccordionSummary
                        aria-controls="emails-to-notify-content"
                        id="emails-to-notify"
                      >
                        {cardTittle('Notificação', 'mail')}
                      </AccordionSummary>
                      <AccordionDetails>
                        <div>
                          {paymentLinkV3Data.emails_to_notify
                            .split(';')
                            .filter((email: string) => email.trim() !== '')
                            .map((email: string, index: number) => (
                              <Box
                                key={index}
                                display="flex"
                                gap={1.25}
                                alignItems="end"
                                margin="10px 0px 10px 0px"
                              >
                                <Typography variant="smLight" color="#777777">
                                  E-mail {index + 1}
                                </Typography>
                                <S.Separator sx={{ marginTop: '4px' }} />
                                <Typography
                                  variant="smBold"
                                  display="flex"
                                  alignItems="center"
                                >
                                  {email.trim()}
                                </Typography>
                              </Box>
                            ))}
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  </div>
                )}

                {/* Contatos */}
                {!paymentLinkV3Data?.customer?.name &&
                  !productLink?.customer?.name &&
                  (paymentLinkV3Data as any)?.link_fields?.contacts?.enabled &&
                  (paymentLinkV3Data as any)?.link_fields?.contacts
                    ?.contacts_fields?.length > 0 && (
                    <div>
                      <Accordion
                        defaultExpanded
                        sx={{
                          border: '1px solid #E8EBE8',
                          borderLeft: '3px solid',
                          borderLeftColor: 'primary.main',
                          borderRadius: '8px !important',
                          backgroundColor: '#FAFBFA',
                          boxShadow: 'none',
                          '&:before': { display: 'none' },
                          '&.Mui-expanded': {
                            margin: 0,
                            backgroundColor: '#fff'
                          }
                        }}
                      >
                        <AccordionSummary
                          aria-controls="contacts-content"
                          id="contacts"
                        >
                          {cardTittle('Contatos', 'supervisorAccount')}
                        </AccordionSummary>
                        <AccordionDetails>
                          <div>
                            <Box maxHeight="200px" overflow="auto">
                              <Table
                                mobileCards
                                header={[
                                  { id: 'type', label: 'Tipo de contato' },
                                  { id: 'required', label: 'Obrigatório' }
                                ]}
                                hoverInRow
                                hiddePagination
                                select={{ useSelect: false }}
                                rows={(
                                  paymentLinkV3Data as any
                                ).link_fields.contacts.contacts_fields.map(
                                  (contact: any) => ({
                                    type:
                                      contact.contact_type === 'financial'
                                        ? 'Financeiro'
                                        : contact.contact_type === 'bank'
                                        ? 'Bancário'
                                        : contact.contact_type === 'commercial'
                                        ? 'Comercial'
                                        : contact.contact_type,
                                    required: contact.required ? 'Sim' : 'Não'
                                  })
                                )}
                              />
                            </Box>
                          </div>
                        </AccordionDetails>
                      </Accordion>
                    </div>
                  )}

                {/* Composição */}
                {(paymentLinkV3Data?.billing_items || productLink?.items) && (
                  <div>
                    <Accordion
                      defaultExpanded
                      sx={{
                        border: '1px solid #E8EBE8',
                        borderLeft: '3px solid',
                        borderLeftColor: 'primary.main',
                        borderRadius: '8px !important',
                        backgroundColor: '#FAFBFA',
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': {
                          margin: 0,
                          backgroundColor: '#fff'
                        }
                      }}
                    >
                      <AccordionSummary
                        aria-controls="composition-content"
                        id="composition"
                        data-cy="composition-accordion-summary"
                      >
                        {cardTittle('Composição', 'paid')}
                      </AccordionSummary>
                      <AccordionDetails>
                        <div>
                          <Box maxHeight="200px" overflow="auto">
                            <Table
                              mobileCards
                              header={[
                                { id: 'item', label: 'Plano/Item' },
                                { id: 'amountUnit', label: 'Valor' },
                                { id: 'bonus', label: 'Bonificação' },
                                { id: 'quantity', label: 'Qtd.' },
                                {
                                  id: 'modifyQuantity',
                                  label: 'Modificar qtde.'
                                },
                                {
                                  id: 'minimumQuantity',
                                  label: 'Qtde. mínima'
                                },
                                { id: 'amountBilled', label: 'Total' }
                              ]}
                              hoverInRow
                              rows={compositionDetailMapper(
                                paymentLinkV3Data?.billing_items ||
                                  productLink?.items ||
                                  []
                              )}
                              select={{ useSelect: false }}
                              hiddePagination
                            />
                          </Box>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  </div>
                )}

                {/* Cupons */}
                {canUseCoupons && detailCoupons.length > 0 && (
                  <div>
                    <CouponsAccordion
                      linkUuid={productLink?.uuid || paymentLinkV3Data?.uuid}
                      couponsEnabled={
                        paymentLinkV3Data?.coupons_enabled ?? false
                      }
                      onCouponsEnabledChange={undefined}
                      associatedCoupons={detailCoupons}
                      onCouponsChange={(coupons) => {
                        setDetailCoupons(coupons)
                      }}
                      isDetails
                    />
                  </div>
                )}

                {/* Configurações de Desconto */}
                {(paymentLinkV3Data?.discount_policy === 'true' ||
                  productLink?.discountPolicy) && (
                  <div>
                    <Accordion
                      sx={{
                        border: '1px solid #E8EBE8',
                        borderLeft: '3px solid',
                        borderLeftColor: 'primary.main',
                        borderRadius: '8px !important',
                        backgroundColor: '#FAFBFA',
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': {
                          margin: 0,
                          backgroundColor: '#fff'
                        }
                      }}
                    >
                      <AccordionSummary
                        aria-controls="discountSettings-content"
                        id="discountSettings"
                        data-cy="discount-settings-accordion-summary"
                      >
                        {cardTittle('Configurações de desconto', 'paid')}
                      </AccordionSummary>
                      <AccordionDetails>
                        <div>
                          <Typography variant="smBold">
                            Desconto configurado
                          </Typography>
                          {/* Aqui você pode adicionar mais detalhes dos descontos quando disponíveis */}
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  </div>
                )}

                {/* Customer/Cliente */}
                {paymentLinkV3Data?.customer && (
                  <div>
                    <Accordion
                      defaultExpanded
                      sx={{
                        border: '1px solid #E8EBE8',
                        borderLeft: '3px solid',
                        borderLeftColor: 'primary.main',
                        borderRadius: '8px !important',
                        backgroundColor: '#FAFBFA',
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': {
                          margin: 0,
                          backgroundColor: '#fff'
                        }
                      }}
                    >
                      <AccordionSummary
                        aria-controls="customer-content"
                        id="customer"
                        data-cy="customer-accordion-summary"
                      >
                        {cardTittle('Detalhes do cliente', 'supervisorAccount')}
                      </AccordionSummary>
                      <AccordionDetails>
                        <div>
                          {/* Nome do cliente */}
                          <Box
                            display="flex"
                            gap={1.25}
                            alignItems="end"
                            margin="10px 0px 10px 0px"
                          >
                            <Typography variant="smLight" color="#777777">
                              Nome:
                            </Typography>
                            <S.Separator sx={{ marginTop: '4px' }} />
                            <Typography
                              variant="smBold"
                              display="flex"
                              alignItems="center"
                            >
                              <a
                                href={`/clientes/${paymentLinkV3Data.customer.uuid}`}
                                target="_blank"
                                style={{ all: 'unset' }}
                              >
                                <Box
                                  display="flex"
                                  flexWrap="wrap"
                                  gap="8px"
                                  alignItems="center"
                                  sx={{ cursor: 'pointer' }}
                                >
                                  <Typography
                                    variant="smBold"
                                    sx={{
                                      textDecoration: 'underline'
                                    }}
                                  >
                                    {paymentLinkV3Data.customer.name}
                                  </Typography>
                                  <Icon
                                    icon="open"
                                    width="21px"
                                    height="21px"
                                    sx={{
                                      cursor: 'pointer',
                                      color: '#CBCBCB'
                                    }}
                                  />
                                </Box>
                              </a>
                            </Typography>
                          </Box>

                          {/* CPF/CNPJ */}
                          {paymentLinkV3Data.customer.document && (
                            <Box
                              display="flex"
                              gap={1.25}
                              alignItems="end"
                              margin="10px 0px 10px 0px"
                            >
                              <Typography variant="smLight" color="#777777">
                                CPF/CNPJ:
                              </Typography>
                              <S.Separator sx={{ marginTop: '4px' }} />
                              <Typography
                                variant="smBold"
                                display="flex"
                                alignItems="center"
                              >
                                {formatCpfCnpj(
                                  paymentLinkV3Data.customer.document
                                )}
                              </Typography>
                            </Box>
                          )}

                          {/* Email */}
                          {paymentLinkV3Data.customer.email && (
                            <Box
                              display="flex"
                              gap={1.25}
                              alignItems="end"
                              margin="10px 0px 10px 0px"
                            >
                              <Typography variant="smLight" color="#777777">
                                E-mail:
                              </Typography>
                              <S.Separator sx={{ marginTop: '4px' }} />
                              <Typography
                                variant="smBold"
                                display="flex"
                                alignItems="center"
                              >
                                {paymentLinkV3Data.customer.email}
                              </Typography>
                            </Box>
                          )}

                          {/* Telefone */}
                          {paymentLinkV3Data.customer.phone && (
                            <Box
                              display="flex"
                              gap={1.25}
                              alignItems="end"
                              margin="10px 0px 10px 0px"
                            >
                              <Typography variant="smLight" color="#777777">
                                Telefone:
                              </Typography>
                              <S.Separator sx={{ marginTop: '4px' }} />
                              <Typography
                                variant="smBold"
                                display="flex"
                                alignItems="center"
                              >
                                {formatPhone(paymentLinkV3Data.customer.phone)}
                              </Typography>
                            </Box>
                          )}

                          {/* Endereço */}
                          {paymentLinkV3Data.customer.address && (
                            <Box
                              display="flex"
                              gap={1.25}
                              alignItems="end"
                              margin="10px 0px 10px 0px"
                            >
                              <Typography variant="smLight" color="#777777">
                                Endereço:
                              </Typography>
                              <S.Separator sx={{ marginTop: '4px' }} />
                              <Typography
                                variant="smBold"
                                display="flex"
                                alignItems="center"
                                sx={{ whiteSpace: 'pre-line' }}
                              >
                                {(() => {
                                  const address =
                                    paymentLinkV3Data.customer.address
                                  const parts = []

                                  if (address.street) parts.push(address.street)
                                  if (address.number) parts.push(address.number)
                                  if (address.complement)
                                    parts.push(address.complement)
                                  if (address.neighborhood)
                                    parts.push(address.neighborhood)
                                  if (address.city && address.state) {
                                    parts.push(
                                      `${address.city} - ${address.state}`
                                    )
                                  }
                                  if (address.postal_code)
                                    parts.push(`CEP: ${address.postal_code}`)

                                  return parts.join(', ')
                                })()}
                              </Typography>
                            </Box>
                          )}
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  </div>
                )}

                {/* Nota Fiscal */}
                {paymentLinkV3Data?.nfse_scheduled && (
                  <div>
                    <Accordion
                      sx={{
                        border: '1px solid #E8EBE8',
                        borderLeft: '3px solid',
                        borderLeftColor: 'primary.main',
                        borderRadius: '8px !important',
                        backgroundColor: '#FAFBFA',
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': {
                          margin: 0,
                          backgroundColor: '#fff'
                        }
                      }}
                    >
                      <AccordionSummary
                        aria-controls="nfse-content"
                        id="nfse"
                        data-cy="nfse-accordion-summary"
                      >
                        {cardTittle('Nota fiscal', 'receipt')}
                      </AccordionSummary>
                      <AccordionDetails>
                        <div>
                          {/* Prestador de serviço */}
                          <Box
                            display="flex"
                            gap={1.25}
                            alignItems="end"
                            margin="10px 0px 10px 0px"
                          >
                            <Typography variant="smLight" color="#777777">
                              Prestador de serviço:
                            </Typography>
                            <S.Separator sx={{ marginTop: '4px' }} />
                            <Typography variant="smBold">
                              {paymentLinkV3Data?.nfse_scheduled?.nfse_issuer
                                ?.legal_name || ''}
                            </Typography>
                          </Box>

                          {/* ISS retido */}
                          <Box
                            display="flex"
                            gap={1.25}
                            alignItems="end"
                            margin="10px 0px 10px 0px"
                          >
                            <Typography variant="smLight" color="#777777">
                              ISS retido:
                            </Typography>
                            <S.Separator sx={{ marginTop: '4px' }} />
                            <Typography variant="smBold">
                              {paymentLinkV3Data?.nfse_scheduled
                                ?.iss_retention === true
                                ? 'Sim'
                                : 'Não'}
                            </Typography>
                          </Box>

                          {/* Emissão */}
                          <Box
                            display="flex"
                            gap={1.25}
                            alignItems="end"
                            margin="10px 0px 10px 0px"
                          >
                            <Typography variant="smLight" color="#777777">
                              Emissão:
                            </Typography>
                            <S.Separator sx={{ marginTop: '4px' }} />
                            <Typography variant="smBold">
                              {issueWhenEnum[
                                paymentLinkV3Data?.nfse_policy as keyof typeof issueWhenEnum
                              ] || paymentLinkV3Data?.nfse_policy}
                            </Typography>
                          </Box>

                          {(paymentLinkV3Data?.nfse_scheduled as any)
                            ?.configuration_type === 1 ||
                          (paymentLinkV3Data?.nfse_scheduled as any)
                            ?.configuration_type === 'specific_day_of_month' ||
                          (paymentLinkV3Data?.nfse_scheduled as any)
                            ?.issue_mode === 'specific_day' ? (
                            <>
                              <Box
                                display="flex"
                                gap={1.25}
                                alignItems="end"
                                margin="10px 0px 10px 0px"
                              >
                                <Typography variant="smLight" color="#777777">
                                  Dia do mês:
                                </Typography>
                                <S.Separator sx={{ marginTop: '4px' }} />
                                <Typography variant="smBold">
                                  {(paymentLinkV3Data?.nfse_scheduled as any)
                                    ?.day_of_month ||
                                    (paymentLinkV3Data?.nfse_scheduled as any)
                                      ?.specific_day ||
                                    ''}
                                </Typography>
                              </Box>
                              <Box
                                display="flex"
                                gap={1.25}
                                alignItems="end"
                                margin="10px 0px 10px 0px"
                              >
                                <Typography variant="smLight" color="#777777">
                                  Mês de emissão:
                                </Typography>
                                <S.Separator sx={{ marginTop: '4px' }} />
                                <Typography variant="smBold">
                                  {(() => {
                                    const relVal = (
                                      paymentLinkV3Data?.nfse_scheduled as any
                                    )?.due_date_relation as string
                                    const mapStr: Record<string, string> = {
                                      two_months_before:
                                        'Dois meses antes do vencimento',
                                      month_before:
                                        'Mês anterior ao vencimento',
                                      same_month: 'Mês do vencimento',
                                      month_after: 'Mês seguinte ao vencimento',
                                      two_months_after:
                                        'Dois meses depois do vencimento'
                                    }
                                    if (relVal) return mapStr[relVal] || ''
                                    const off = (
                                      paymentLinkV3Data?.nfse_scheduled as any
                                    )?.specific_month_offset
                                    const offMap: Record<string, string> = {
                                      '-2': mapStr.two_months_before,
                                      '-1': mapStr.month_before,
                                      '0': mapStr.same_month,
                                      '1': mapStr.month_after,
                                      '2': mapStr.two_months_after
                                    }
                                    return offMap[String(off)] || ''
                                  })()}
                                </Typography>
                              </Box>
                            </>
                          ) : null}

                          {/* Item de serviço */}
                          <Box
                            display="flex"
                            gap={1.25}
                            alignItems="end"
                            margin="10px 0px 10px 0px"
                          >
                            <Typography variant="smLight" color="#777777">
                              Item de serviço:
                            </Typography>
                            <S.Separator sx={{ marginTop: '4px' }} />
                            <Typography variant="smBold">
                              {paymentLinkV3Data?.nfse_scheduled
                                ?.nfse_issuer_service?.code || ''}{' '}
                              -{' '}
                              {typeof paymentLinkV3Data?.nfse_scheduled
                                ?.nfse_issuer_service?.description === 'string'
                                ? paymentLinkV3Data?.nfse_scheduled
                                    ?.nfse_issuer_service?.description
                                : paymentLinkV3Data?.nfse_scheduled
                                    ?.nfse_issuer_service?.description?.title ||
                                  paymentLinkV3Data?.nfse_scheduled
                                    ?.nfse_issuer_service?.description
                                    ?.description ||
                                  ''}
                            </Typography>
                          </Box>

                          {/* Valor da nota */}
                          <Box
                            display="flex"
                            gap={1.25}
                            alignItems="end"
                            margin="10px 0px 10px 0px"
                          >
                            <Typography variant="smLight" color="#777777">
                              Valor da nota:
                            </Typography>
                            <S.Separator sx={{ marginTop: '4px' }} />
                            <Typography variant="smBold">
                              {amountTypeEnum[
                                paymentLinkV3Data?.nfse_scheduled
                                  ?.amount_type as keyof typeof amountTypeEnum
                              ] ||
                                paymentLinkV3Data?.nfse_scheduled
                                  ?.amount_type ||
                                ''}{' '}
                              {paymentLinkV3Data?.nfse_scheduled?.amount
                                ? `- ${formatMoney(
                                    Number(
                                      paymentLinkV3Data?.nfse_scheduled?.amount
                                    )
                                  )}`
                                : ''}
                            </Typography>
                          </Box>

                          {/* Data/Hora emissão - só mostra se tiver sido emitida */}
                          {paymentLinkV3Data?.nfse_scheduled?.created_at && (
                            <Box
                              display="flex"
                              gap={1.25}
                              alignItems="end"
                              margin="10px 0px 10px 0px"
                            >
                              <Typography variant="smLight" color="#777777">
                                Data/Hora emissão:
                              </Typography>
                              <S.Separator sx={{ marginTop: '4px' }} />
                              <Typography variant="smBold">
                                {moment(
                                  paymentLinkV3Data?.nfse_scheduled?.created_at
                                ).format('DD/MM/YYYY HH:mm:ss')}
                              </Typography>
                            </Box>
                          )}

                          {/* Natureza de operação */}
                          <Box
                            display="flex"
                            gap={1.25}
                            alignItems="end"
                            margin="10px 0px 10px 0px"
                          >
                            <Typography variant="smLight" color="#777777">
                              Natureza de operação:
                            </Typography>
                            <S.Separator sx={{ marginTop: '4px' }} />
                            <Typography variant="smBold">
                              {natureOperationEnum[
                                paymentLinkV3Data?.nfse_scheduled
                                  ?.nature_operation as keyof typeof natureOperationEnum
                              ] ||
                                paymentLinkV3Data?.nfse_scheduled
                                  ?.nature_operation ||
                                ''}
                            </Typography>
                          </Box>

                          {paymentLinkV3Data?.nfse_scheduled
                            ?.copy_description ? (
                            <Box
                              display="flex"
                              gap={1.25}
                              alignItems="end"
                              margin={'10px 0px 10px 0px'}
                            >
                              <Typography variant="smLight" color="#777777">
                                Copiar descrição da cobrança
                              </Typography>
                              <S.Separator />
                              <Typography variant="smBold">
                                <Typography variant="smBold">
                                  {paymentLinkV3Data?.nfse_scheduled
                                    ?.copy_description
                                    ? 'Sim'
                                    : 'Não'}
                                </Typography>
                              </Typography>
                            </Box>
                          ) : (
                            paymentLinkV3Data?.nfse_scheduled?.description && (
                              <Box display="flex" flexDirection="column">
                                <Typography
                                  variant="smLight"
                                  color="#777777"
                                  mb="6px"
                                >
                                  Descrição:
                                </Typography>
                                <Typography variant="smBold">
                                  {
                                    paymentLinkV3Data?.nfse_scheduled
                                      ?.description
                                  }
                                </Typography>
                              </Box>
                            )
                          )}
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  </div>
                )}

                {/* Negativação */}
                {(paymentLinkV3Data?.billing_no_payment_report_scheduled
                  ?.days_to_report ||
                  productLink?.daysToReport) && (
                  <div>
                    <Accordion
                      sx={{
                        border: '1px solid #E8EBE8',
                        borderLeft: '3px solid',
                        borderLeftColor: 'primary.main',
                        borderRadius: '8px !important',
                        backgroundColor: '#FAFBFA',
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': {
                          margin: 0,
                          backgroundColor: '#fff'
                        }
                      }}
                    >
                      <AccordionSummary
                        aria-controls="negativation-content"
                        id="negativation"
                        data-cy="negativation-accordion-summary"
                      >
                        {cardTittle('Detalhes da negativação', 'thumbDownAlt')}
                      </AccordionSummary>
                      <AccordionDetails>
                        <div>
                          <Box
                            display="flex"
                            gap={1.25}
                            alignItems="end"
                            margin="10px 0px 10px 0px"
                          >
                            <Typography variant="smLight" color="#777777">
                              Negativar após:
                            </Typography>
                            <S.Separator sx={{ marginTop: '4px' }} />
                            <Typography variant="smBold">
                              {paymentLinkV3Data
                                ?.billing_no_payment_report_scheduled
                                ?.days_to_report ||
                                productLink?.daysToReport}{' '}
                              dias
                            </Typography>
                          </Box>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  </div>
                )}

                {/* Identificadores Internos */}
                {paymentLinkV3Data?.metadata &&
                  Object.keys(paymentLinkV3Data.metadata).length > 0 && (
                    <div data-cy="internal-identifier-accordion">
                      <Accordion
                        sx={{
                          border: '1px solid #E8EBE8',
                          borderLeft: '3px solid',
                          borderLeftColor: 'primary.main',
                          borderRadius: '8px !important',
                          backgroundColor: '#FAFBFA',
                          boxShadow: 'none',
                          '&:before': { display: 'none' },
                          '&.Mui-expanded': {
                            margin: 0,
                            backgroundColor: '#fff'
                          }
                        }}
                      >
                        <AccordionSummary
                          aria-controls="internalIdentifiers-content"
                          id="internalIdentifiers"
                          data-cy="internal-identifier-accordion-summary"
                        >
                          {cardTittle('Identificador interno', 'bookmark')}
                        </AccordionSummary>
                        <AccordionDetails>
                          {Object.keys(paymentLinkV3Data?.metadata).map(
                            (field) => (
                              <Box
                                key={field}
                                display="flex"
                                gap={1.25}
                                alignItems="end"
                                margin="10px 0px 10px 0px"
                              >
                                <Typography variant="smLight" color="#777777">
                                  {field}
                                </Typography>
                                <S.Separator sx={{ marginTop: '4px' }} />
                                <Typography variant="smBold">
                                  {typeof paymentLinkV3Data?.metadata[field] ===
                                  'string'
                                    ? paymentLinkV3Data?.metadata[field]
                                    : typeof paymentLinkV3Data?.metadata[
                                        field
                                      ] === 'object' &&
                                      paymentLinkV3Data?.metadata[field] !==
                                        null
                                    ? JSON.stringify(
                                        paymentLinkV3Data?.metadata[field],
                                        null,
                                        2
                                      )
                                    : String(
                                        paymentLinkV3Data?.metadata[field] || ''
                                      )}
                                </Typography>
                              </Box>
                            )
                          )}
                        </AccordionDetails>
                      </Accordion>
                    </div>
                  )}

                {/* Reajuste */}
                {paymentLinkV3Data?.adjustment_scheduled && (
                  <div>
                    <Accordion
                      defaultExpanded
                      sx={{
                        border: '1px solid #E8EBE8',
                        borderLeft: '3px solid',
                        borderLeftColor: 'primary.main',
                        borderRadius: '8px !important',
                        backgroundColor: '#FAFBFA',
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': {
                          margin: 0,
                          backgroundColor: '#fff'
                        }
                      }}
                    >
                      <AccordionSummary
                        aria-controls="adjustmentDetails-content"
                        id="adjustmentDetails"
                        data-cy="adjustment-accordion-summary"
                      >
                        {cardTittle('Reajuste', 'add')}
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box
                          display="flex"
                          gap={1.25}
                          alignItems="end"
                          margin="10px 0px 10px 0px"
                        >
                          <Typography
                            variant="smLight"
                            color={theme.palette.neutral[400]}
                          >
                            Tipo de reajuste
                          </Typography>
                          <S.Separator sx={{ marginTop: '4px' }} />
                          <Typography variant="smBold">
                            {getAdjustmentTypeLabel(
                              paymentLinkV3Data.adjustment_scheduled
                                .adjustment_type
                            )}
                          </Typography>
                        </Box>
                        <Box
                          display="flex"
                          gap={1.25}
                          alignItems="end"
                          margin="10px 0px 10px 0px"
                        >
                          <Typography
                            variant="smLight"
                            color={theme.palette.neutral[400]}
                          >
                            Frequência
                          </Typography>
                          <S.Separator sx={{ marginTop: '4px' }} />
                          <Typography variant="smBold">
                            {getAdjustmentFrequencyLabel(
                              paymentLinkV3Data.adjustment_scheduled.frequency
                            )}
                          </Typography>
                        </Box>
                        {paymentLinkV3Data.adjustment_scheduled
                          .adjustment_type === 'customized' &&
                          paymentLinkV3Data.adjustment_scheduled
                            .percentage_value && (
                            <Box
                              display="flex"
                              gap={1.25}
                              alignItems="end"
                              margin="10px 0px 10px 0px"
                            >
                              <Typography
                                variant="smLight"
                                color={theme.palette.neutral[400]}
                              >
                                Percentual
                              </Typography>
                              <S.Separator sx={{ marginTop: '4px' }} />
                              <Typography variant="smBold">
                                {Number(
                                  paymentLinkV3Data.adjustment_scheduled
                                    .percentage_value
                                )
                                  .toFixed(2)
                                  .replace('.', ',')}
                                %
                              </Typography>
                            </Box>
                          )}
                        <Box
                          display="flex"
                          gap={1.25}
                          alignItems="end"
                          margin="10px 0px 10px 0px"
                        >
                          <Typography
                            variant="smLight"
                            color={theme.palette.neutral[400]}
                          >
                            Próximo reajuste
                          </Typography>
                          <S.Separator sx={{ marginTop: '4px' }} />
                          <Typography variant="smBold">
                            {getAdjustmentRelativeLabel(
                              paymentLinkV3Data.adjustment_scheduled.frequency
                            )}
                          </Typography>
                        </Box>
                        <Box
                          display="flex"
                          gap={1.25}
                          alignItems="end"
                          margin="10px 0px 10px 0px"
                        >
                          <Typography
                            variant="smLight"
                            color={theme.palette.neutral[400]}
                          >
                            Reajustar automaticamente
                          </Typography>
                          <S.Separator sx={{ marginTop: '4px' }} />
                          <Typography variant="smBold">
                            {paymentLinkV3Data.adjustment_scheduled
                              .automatic_application
                              ? 'Sim'
                              : 'Não'}
                          </Typography>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </div>
                )}

                {/* Histórico */}
                {paymentLinkV3Data?.historic &&
                  paymentLinkV3Data.historic.length > 0 && (
                    <div>
                      <Accordion
                        sx={{
                          border: '1px solid #E8EBE8',
                          borderLeft: '3px solid',
                          borderLeftColor: 'primary.main',
                          borderRadius: '8px !important',
                          backgroundColor: '#FAFBFA',
                          boxShadow: 'none',
                          '&:before': { display: 'none' },
                          '&.Mui-expanded': {
                            margin: 0,
                            backgroundColor: '#fff'
                          }
                        }}
                      >
                        <AccordionSummary
                          aria-controls="historic-content"
                          id="historic"
                        >
                          {cardTittle(
                            'Histórico',
                            'article',
                            `${theme.palette.neutral[200]}`
                          )}
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box width="100%" overflow="auto">
                            <Table
                              mobileCards
                              header={[
                                { id: 'event', label: 'Ação' },
                                { id: 'description', label: 'Descrição' },
                                { id: 'date_change', label: 'Data' },
                                { id: 'user', label: 'Usuário' }
                              ]}
                              rows={historicChangesMapper(
                                paymentLinkV3Data.historic as HistoricType
                              )}
                              select={{ useSelect: false }}
                              hiddePagination
                            />
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </div>
                  )}
              </S.AccordionWrapper>
            </ModalContent>
          ) : (
            <ModalContent>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <Skeleton variant="rectangular" width="100%" height="400px" />
              </Box>
            </ModalContent>
          )}

          <ModalFooter>
            <Stack
              direction={isMobile ? 'column' : 'row'}
              gap={1}
              width="100%"
              justifyContent="flex-end"
              alignItems={isMobile ? 'stretch' : 'center'}
            >
              {/* TagsPopover positioned to the right */}
              <Stack direction="row" alignItems="center">
                <TagsPopover
                  control={control}
                  name="tags"
                  saveMode="immediate"
                  saveContext={{
                    taggableType: 'ProductLink',
                    taggableUuid:
                      productLink?.uuid || paymentLinkV3Data?.uuid || ''
                  }}
                  onSave={async () => {
                    // Show success notification
                    toast.success('Tags atualizadas com sucesso!')
                    // Reload the payment link to refresh data
                    await fetchPaymentLinkDetails()
                    // Trigger table update if needed
                    updateTable?.()
                  }}
                  maxTags={10}
                />
              </Stack>

              {/* Action buttons container */}
              <Stack
                direction={isMobile ? 'column' : 'row'}
                gap={1}
                width={isMobile ? '100%' : 'auto'}
              >
                <Can permission="product_links:statistics">
                  <Button
                    variantButton="neutral"
                    fullWidth={isMobile}
                    onClick={() => setIsOpenModalPaymentReport(true)}
                  >
                    Relatórios
                  </Button>
                </Can>
                <Can permission="product_links:manage">
                  <Button
                    variantButton="neutral"
                    fullWidth={isMobile}
                    onClick={handleDuplicate}
                  >
                    Duplicar
                  </Button>
                </Can>
                <Can permission="product_links:manage">
                  <Button
                    variantButton="neutral"
                    fullWidth={isMobile}
                    onClick={() => setIsEditModalOpen(true)}
                  >
                    Editar
                  </Button>
                </Can>
                <Can permission="product_links:show">
                  <CopyLinkPopover
                    linkUrl={productLink?.link || paymentLinkV3Data?.link}
                    coupons={detailCoupons}
                  >
                    {({ onClick }) => (
                      <Button
                        variantButton="neutral"
                        fullWidth={isMobile}
                        onClick={onClick}
                      >
                        Copiar Link
                      </Button>
                    )}
                  </CopyLinkPopover>
                </Can>
                <Can permission="product_links:show">
                  <Button onClick={() => handleOpen()} fullWidth={isMobile}>
                    Abrir Link
                  </Button>
                </Can>
              </Stack>
            </Stack>
          </ModalFooter>
        </ModalBody>
      </Modal>

      {/* Modal de Edição */}
      <ModalNewPaymentLink
        isOpen={isEditModalOpen}
        handleClose={(refresh?: boolean) => {
          setIsEditModalOpen(false)
          // Se houve edição, recarregar os dados do modal de detalhes e atualizar a tabela
          if (refresh && productLink?.uuid) {
            // Recarregar os dados do modal de detalhes
            fetchPaymentLinkDetails()
            // Atualizar a tabela também
            updateTable && updateTable()
          }
        }}
        paymentLink={paymentLinkV3Data || (productLink as any)}
        isEdit={true}
      />

      {/* Modal de Relatório (aberto a partir do botão "Relatórios") */}
      <ModalPaymentReport
        uuid={productLink?.uuid || paymentLinkV3Data?.uuid || ''}
        isOpenModalPaymentReport={isOpenModalPaymentReport}
        handleCloseModalPaymentReport={() => setIsOpenModalPaymentReport(false)}
      />

      {/* Modal de Duplicação */}
      <ModalNewPaymentLink
        isOpen={isDuplicateModalOpen}
        handleClose={(refresh?: boolean, newPaymentLink?: any) => {
          setIsDuplicateModalOpen(false)
          // Se houve duplicação, atualizar a tabela
          if (refresh) {
            updateTable && updateTable()
            // Se um novo payment link foi criado, atualizar os dados do modal atual
            if (newPaymentLink) {
              setTimeout(() => {
                setPaymentLinkV3Data(newPaymentLink)
              }, 300)
            }
          }
        }}
        paymentLink={paymentLinkV3Data || (productLink as any)}
        isEdit={true}
        isDuplication={true}
      />
    </>
  )
}

export default ModalPaymentLinkDetail
